import { isSystemDataSequenceId } from "building-types/dataSequences/isSystemDataSequenceId";
import type { Doc } from "building-types/doc";
import type { DocEntityHandle } from "building-types/handles/docEntityHandleTypes";
import { docEntityIsReadonly } from "../docEntityReadonly";
import { isBuiltInLabel } from "../utils/builtInLabelUtils";
import { docGetGroupImmediateChildren } from "./docGetGroupImmediateChildren";
import { docGetTableEntity } from "./docGetTableEntity";

export type DuplicableEntityHandle = Extract<
  DocEntityHandle,
  {
    type:
      | "ANNOTATION"
      | "BLOCK"
      | "CASEWORK"
      | "CEILING"
      | "COLUMN"
      | "COMPONENT_INSTANCE"
      | "COMPONENT"
      | "COMPOSITE_LEVEL"
      | "CONTENT_TEMPLATE"
      | "CONTENT_TEMPLATE_INSTANCE"
      | "DATA_SEQUENCE"
      | "DATA_TABLE"
      | "DORMER"
      | "DRAWING_REF"
      | "EDGE_BEHAVIOR"
      | "ELECTRICAL_DEVICE"
      | "ELECTRICAL_LAYOUT"
      | "ELECTRICAL_RULE"
      | "ELEVATION"
      | "FACADE"
      | "FACADE_SPAN_STRATUM"
      | "FACADE_SWEEP_STRATUM"
      | "FLOOR"
      | "FLOOR_FINISH"
      | "FLOOR_SYSTEM"
      | "FOUNDATION"
      | "FOUNDATION_ASSEMBLY"
      | "FOUNDATION_COMPONENT"
      | "FOUNDATION_SYSTEM"
      | "FRAME_FLOOR_ASSEMBLY"
      | "FRAMING_COMPONENT"
      | "LABEL"
      | "CIP_COMPONENT"
      | "MASONRY_COMPONENT"
      | "ROOF_OPENING"
      | "SHEATHING_COMPONENT"
      | "SLAB_COMPONENT"
      | "SLAB_FLOOR_ASSEMBLY"
      | "GROUP"
      | "MEASUREMENT"
      | "MODIFIER"
      | "OPENING"
      | "OPTION_RULE"
      | "OPTION_SET"
      | "OPTION_TEMPLATE"
      | "PALETTE"
      | "LOCAL_PARAMETER"
      | "GLOBAL_PARAMETER"
      | "PLAN_SET_TEMPLATE"
      | "PLAN_SET_TEMPLATE_CONFIG"
      | "PLAN_VIEW"
      | "PRESET"
      | "PROFILE"
      | "PROP"
      | "PROP_INSTANCE"
      | "RAILING"
      | "RAILING_SETTING"
      | "RAILING_POST"
      | "FREEFORM_WALL"
      | "REFERENCE_GEOM"
      | "ROOF"
      | "ROOF_SYSTEM"
      | "ROOF_VOLUME"
      | "ROOM"
      | "ROOM_LAYOUT"
      | "ROOM_LAYOUT_INSTANCE"
      | "SECTION_VIEW"
      | "SETTINGS_SET"
      | "SHEET"
      | "SITE"
      | "SITE_SHAPE"
      | "SKYLIGHT"
      | "STAIR_BLOCK"
      | "STAIR_SETTING"
      | "STAIR_SYSTEM"
      | "UNDERLAY"
      | "VIEW_3D"
      | "VIEW_TEMPLATE"
      | "VIEWPORT"
      | "WALL_PLANE_RULE"
      | "DATUM";
  }
> & { entityId?: string };

export type DuplicableEntityType = DuplicableEntityHandle["type"];

export const duplicableEntityTypes: { [K in DuplicableEntityHandle["type"]]: true } = {
  ANNOTATION: true,
  BLOCK: true,
  CASEWORK: true,
  CEILING: true,
  COLUMN: true,
  COMPONENT_INSTANCE: true,
  COMPONENT: true,
  COMPOSITE_LEVEL: true,
  CONTENT_TEMPLATE: true,
  CONTENT_TEMPLATE_INSTANCE: true,
  DATA_SEQUENCE: true,
  DATA_TABLE: true,
  DORMER: true,
  DRAWING_REF: true,
  EDGE_BEHAVIOR: true,
  ELECTRICAL_DEVICE: true,
  ELECTRICAL_LAYOUT: true,
  ELECTRICAL_RULE: true,
  ELEVATION: true,
  FACADE: true,
  FACADE_SPAN_STRATUM: true,
  FACADE_SWEEP_STRATUM: true,
  FLOOR: true,
  FLOOR_FINISH: true,
  FLOOR_SYSTEM: true,
  FOUNDATION: true,
  FOUNDATION_ASSEMBLY: true,
  FOUNDATION_COMPONENT: true,
  FOUNDATION_SYSTEM: true,
  FRAME_FLOOR_ASSEMBLY: true,
  FRAMING_COMPONENT: true,
  LABEL: true,
  CIP_COMPONENT: true,
  MASONRY_COMPONENT: true,
  ROOF_OPENING: true,
  SHEATHING_COMPONENT: true,
  SLAB_COMPONENT: true,
  SLAB_FLOOR_ASSEMBLY: true,
  GROUP: true,
  MEASUREMENT: true,
  MODIFIER: true,
  OPENING: true,
  OPTION_RULE: true,
  OPTION_SET: true,
  OPTION_TEMPLATE: true,
  PALETTE: true,
  LOCAL_PARAMETER: true,
  GLOBAL_PARAMETER: true,
  PLAN_SET_TEMPLATE: true,
  PLAN_SET_TEMPLATE_CONFIG: true,
  PLAN_VIEW: true,
  PRESET: true,
  PROFILE: true,
  PROP: true,
  PROP_INSTANCE: true,
  RAILING: true,
  RAILING_SETTING: true,
  RAILING_POST: true,
  FREEFORM_WALL: true,
  REFERENCE_GEOM: true,
  ROOF: true,
  ROOF_SYSTEM: true,
  ROOF_VOLUME: true,
  ROOM: true,
  ROOM_LAYOUT: true,
  ROOM_LAYOUT_INSTANCE: true,
  SECTION_VIEW: true,
  SETTINGS_SET: true,
  SHEET: true,
  SITE: true,
  SITE_SHAPE: true,
  SKYLIGHT: true,
  STAIR_BLOCK: true,
  STAIR_SETTING: true,
  STAIR_SYSTEM: true,
  UNDERLAY: true,
  VIEW_3D: true,
  VIEW_TEMPLATE: true,
  VIEWPORT: true,
  WALL_PLANE_RULE: true,
  DATUM: true,
};

export function docEntityIsDuplicable(doc: Doc, handle: DocEntityHandle): handle is DuplicableEntityHandle {
  if (docEntityIsReadonly(doc, handle, "DUPLICATE")) {
    return false;
  }

  switch (handle.type) {
    case "GROUP": {
      const children = docGetGroupImmediateChildren(doc, handle);

      // Prevent the duplication of block groups. Any block disqualifies the group, not just the
      // first: a block copies via an interactive cmd, so a mixed group could only duplicate in part.
      if (!children.length || children.some((child) => child.type === "BLOCK")) {
        return false;
      }

      return children.every((child) => docEntityIsDuplicable(doc, child));
    }
    case "OPTION_SET": {
      const optionSet = docGetTableEntity(doc, handle);
      return optionSet?.optionSetType === "USER";
    }
    case "LABEL": {
      return !isBuiltInLabel(handle.id);
    }
    case "SYSTEM_PARAMETER": {
      return false;
    }
    case "DATA_SEQUENCE": {
      return !isSystemDataSequenceId(handle.id);
    }
    default: {
      return handle.type in duplicableEntityTypes;
    }
  }
}
